import React from 'react';
import { Button, Tag, Tooltip } from 'antd';
import { ApartmentOutlined, ArrowsAltOutlined } from '@ant-design/icons';
import HelpIco from '@icons/common/help.svg';
import {
  InfoContainer,
  InfoHead,
  InfoHeadContainer,
  InfoAlertID,
  InfoAlertMetric,
  InfoAlertMetricCollapse,
  InfoAlertDetailSection,
  InfoAlertCohortSection,
  InfoAlertBody,
  InfoAlertSectionTitle,
  InfoAlertDetailAllItems,
  InfoAlertDetailItem,
  InfoAlertValue,
  InfoAlertCohortSectionBody,
  InfoAlertCohortItem,
  InfoHeadCollapse,
  InfoAlertRootCauseValue,
  CollapsedAlertInfo,
  CollapsedInfoLine,
  CollapseInfoAlertDetailItem,
  CollapseInfoAlertValue,
  CollapseInfoAlertSectionTitle,
  CollapseInfoAlertCohortSectionBody,
  CollapseInfoAlertRootCause,
  InfoAlertSectionRootCauseTitle,
  HelpLinkInToolTip,
} from './styled';
import { useGetAIAlertType, AI_ALERT_TYPE } from '@components/ai-alerts/hooks/useGetAIAlertType';
import { AI_ALERT_MINS_WITH_X_METRICS_HELP } from '@src/utils/help/links';
import { MINS_WITH_X_METRICS_WITH_FF } from '@src/components/ai-alerts/constants';
import { globalFeatureNames } from '@src/utils/featureToggle/constants';
import { useGlobalFeature } from '@src/globalStore/globalFeatureFlagsStore';
import { useSetAtom } from 'jotai';
import { aiCohortDrawerAtom } from '@src/components/ai-alerts/HeaderPanel/SensitivityControl/atoms';
import { isAppModule, isEiModule, PRODUCT_MODE_MAP } from '@src/utils/constants';
import { getMetricMetaByKey } from '@src/utils/metrics';
import useAIAlertMetricsMeta from '@src/components/ai-alerts/hooks/useAIAlertMetricsMeta';
import { useGetAIAlertMetricsCategories } from '@src/components/ai-alerts/hooks/useAIAlertMetricsCategory';

export default function AIAlertExpanded({ info, isExpand, setIsExpand, isFlow }) {
  const alertType = useGetAIAlertType(info.metricName);
  const enableDPICustomDimension = useGlobalFeature(globalFeatureNames.DPI_CUSTOM_DIMENSION);
  const setOpenAICohortDrawer = useSetAtom(aiCohortDrawerAtom);
  const shouldFetchFlowData = isFlow && isAppModule;
  const { aiAlertMetricsMeta = [] } = useAIAlertMetricsMeta(shouldFetchFlowData, []);
  const { categoryMap } = useGetAIAlertMetricsCategories({
    aiAlertMetricsMeta: shouldFetchFlowData ? aiAlertMetricsMeta : [],
    searchText: '',
    product: PRODUCT_MODE_MAP.APP,
    withOneFlowCategory: true,
  });

  const resolveFlowMetrics = (metricName, categories) => {
    const metricMeta = getMetricMetaByKey({ metricKey: metricName });
    const resolvedKey = metricMeta?.flowID || metricName;
    const flowCategory = (categories || []).find((category) => category.isFlow);
    const flowCategoryMetrics = flowCategory?.metrics || [];
    const matchedFlow = flowCategoryMetrics.find((flow) => flow.name === resolvedKey);
    const resolvedMetrics = matchedFlow?.metrics || [metricName];
    return { key: resolvedKey, metrics: resolvedMetrics };
  };

  const HelpComp = MINS_WITH_X_METRICS_WITH_FF.includes(info.metricName) ? (
    <Tooltip
      placement="bottom"
      title={() => {
        return (
          <span>
            Mins with X metrics help analyze issues with HTTP response code processing, client-side
            request handling, and service-side request failures based on the frequency of impacted
            1-minute intervals. These metrics are supported in Trends time series/drill-downs and AI
            alerts, but do not support Table Compare, Filter Compare, Metric Filter, and Peak Value.
            <HelpLinkInToolTip
              href={AI_ALERT_MINS_WITH_X_METRICS_HELP}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more
            </HelpLinkInToolTip>
          </span>
        );
      }}
    >
      <HelpIco style={{ marginLeft: '4px' }} />
    </Tooltip>
  ) : null;

  const handleAICohortClick = (e) => {
    e.stopPropagation(); // Prevent any parent element click handlers

    const { key, metrics } = resolveFlowMetrics(info.metricName, categoryMap);

    setOpenAICohortDrawer({
      open: true,
      metrics,
      key,
      isFlow,
    });
  };

  const AICohortDrawerButton = () => (
    <Tooltip title="Go to AI Cohorts">
      <Button
        onClick={handleAICohortClick}
        type="primary"
        style={{ fontSize: '13px', border: 'none', height: 'inherit', gap: '2px' }}
        ghost
      >
        <ApartmentOutlined />
        Click to improve RCL
      </Button>
    </Tooltip>
  );

  const enableDPIAlertPagination = useGlobalFeature(globalFeatureNames.DPI_ALERT_PAGINATION);
  const isPaginationEnabled = enableDPIAlertPagination;

  return isExpand ? (
    <InfoContainer>
      <InfoHead>
        <span>
          <InfoAlertID>Alert ID: {info.alertId}</InfoAlertID>
          <InfoAlertMetric>
            {info.metricDisplayName}
            {info.kpiName ? (
              <Tag color="purple" style={{ marginLeft: 6 }}>
                {info.kpiName}
              </Tag>
            ) : null}
            {HelpComp}
          </InfoAlertMetric>
          {'status' in info ? <Tag color="blue">{info.alertEndedText}</Tag> : ''}
        </span>
        <InfoHeadCollapse
          onClick={() => {
            setIsExpand(false);
          }}
        >
          Last 30 days
        </InfoHeadCollapse>
      </InfoHead>
      <InfoAlertBody>
        <InfoAlertDetailSection>
          <InfoAlertSectionTitle>Alert Detail</InfoAlertSectionTitle>
          <InfoAlertDetailAllItems>
            <InfoAlertDetailItem>
              <InfoAlertSectionTitle>Duration</InfoAlertSectionTitle>
              <InfoAlertValue>{info.duration}</InfoAlertValue>
              <div>
                {info.issueStartTime} to {info.issueEndTime}
              </div>
            </InfoAlertDetailItem>
            <InfoAlertDetailItem>
              <InfoAlertSectionTitle>
                {alertType === AI_ALERT_TYPE.DIP ? 'Minimum' : 'Peak'} Value {HelpComp}
              </InfoAlertSectionTitle>
              <InfoAlertValue>{info.peakValue ?? 'NA'}</InfoAlertValue>
            </InfoAlertDetailItem>

            {isEiModule || (isPaginationEnabled && info.metricUnit !== '%') ? (
              <>
                <InfoAlertDetailItem>
                  <InfoAlertSectionTitle>Impacted Devices</InfoAlertSectionTitle>
                  <InfoAlertValue>
                    {info.cumulativeImpactedUniques && info.impactedPercentage
                      ? `${info.cumulativeImpactedUniques} / ${info.impactedPercentage}%`
                      : 'NA'}
                  </InfoAlertValue>
                </InfoAlertDetailItem>
                <InfoAlertDetailItem>
                  <InfoAlertSectionTitle>Total Devices</InfoAlertSectionTitle>
                  <InfoAlertValue>{info.cumulativeTotalUniques ?? 'NA'}</InfoAlertValue>
                </InfoAlertDetailItem>
              </>
            ) : (
              <>
                <InfoAlertDetailItem>
                  <InfoAlertSectionTitle>Impacted Devices</InfoAlertSectionTitle>
                  <InfoAlertValue>
                    {info.cumulativeImpactedUniques && info.impactedPercentage
                      ? `${info.cumulativeImpactedUniques} / ${info.impactedPercentage}%`
                      : 'NA'}
                  </InfoAlertValue>
                  <div>Total Devices: {info.cumulativeTotalUniques ?? 'NA'}</div>
                </InfoAlertDetailItem>
                <InfoAlertDetailItem>
                  <InfoAlertSectionTitle>Impacted Count</InfoAlertSectionTitle>
                  <InfoAlertValue>
                    {info.impactedCounts && info.impactedCountPercentage
                      ? `${info.impactedCounts} / ${info.impactedCountPercentage}%`
                      : 'NA'}
                  </InfoAlertValue>
                  <div>Total Counts: {info.totalCounts ?? 'NA'}</div>
                </InfoAlertDetailItem>
              </>
            )}
          </InfoAlertDetailAllItems>
        </InfoAlertDetailSection>
        <InfoAlertCohortSection>
          <InfoAlertSectionTitle>
            Root Cause Leads <AICohortDrawerButton />
          </InfoAlertSectionTitle>
          <InfoAlertCohortSectionBody>
            {info.displayRootCauses.map((item, index) => (
              <InfoAlertCohortItem
                key={index}
                style={{ maxWidth: `${100 / info.displayRootCauses.length}%` }}
              >
                <Tooltip title={item.rootCause} placement="top">
                  <InfoAlertSectionRootCauseTitle>{item.rootCause}</InfoAlertSectionRootCauseTitle>
                </Tooltip>
                <Tooltip title={item.value} placement="bottom">
                  <InfoAlertRootCauseValue>{item.value}</InfoAlertRootCauseValue>
                </Tooltip>
              </InfoAlertCohortItem>
            ))}
          </InfoAlertCohortSectionBody>
        </InfoAlertCohortSection>
      </InfoAlertBody>
    </InfoContainer>
  ) : (
    <CollapsedAlertInfo>
      <InfoHead>
        <InfoHeadContainer>
          <InfoAlertID>Alert ID: {info.alertId}</InfoAlertID>
          <Tooltip title={info.metricDisplayName} placement="top">
            <InfoAlertMetricCollapse>
              {info.metricDisplayName}
              {info.kpiName ? (
                <Tag color="purple" style={{ marginLeft: 6 }}>
                  {info.kpiName}
                </Tag>
              ) : null}
              {HelpComp}
            </InfoAlertMetricCollapse>
          </Tooltip>
          {'status' in info ? <Tag color="blue">{info.alertEndedText}</Tag> : ''}
        </InfoHeadContainer>
        <ArrowsAltOutlined
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setIsExpand(true);
          }}
        />
      </InfoHead>
      <CollapsedInfoLine>
        <CollapseInfoAlertDetailItem>
          <InfoAlertSectionTitle>Duration</InfoAlertSectionTitle>
          <CollapseInfoAlertValue>{info.duration}</CollapseInfoAlertValue>
          <div>
            {info.issueStartTime} to {info.issueEndTime}
          </div>
        </CollapseInfoAlertDetailItem>
        <CollapseInfoAlertDetailItem>
          <InfoAlertSectionTitle>
            {alertType === AI_ALERT_TYPE.DIP ? 'Minimum' : 'Peak'} Value {HelpComp}
          </InfoAlertSectionTitle>
          <CollapseInfoAlertValue>{info.peakValue ?? 'NA'}</CollapseInfoAlertValue>
        </CollapseInfoAlertDetailItem>
      </CollapsedInfoLine>
      <CollapsedInfoLine>
        {isEiModule || (isPaginationEnabled && info.metricUnit !== '%') ? (
          <>
            <CollapseInfoAlertDetailItem>
              <InfoAlertSectionTitle>Impacted Devices</InfoAlertSectionTitle>
              <CollapseInfoAlertValue>
                {info.cumulativeImpactedUniques && info.impactedPercentage
                  ? `${info.cumulativeImpactedUniques} / ${info.impactedPercentage}%`
                  : 'NA'}
              </CollapseInfoAlertValue>
            </CollapseInfoAlertDetailItem>
            <CollapseInfoAlertDetailItem>
              <InfoAlertSectionTitle>Total Devices</InfoAlertSectionTitle>
              <CollapseInfoAlertValue>{info.cumulativeTotalUniques ?? 'NA'}</CollapseInfoAlertValue>
            </CollapseInfoAlertDetailItem>
          </>
        ) : (
          <>
            <CollapseInfoAlertDetailItem>
              <InfoAlertSectionTitle>Impacted Devices</InfoAlertSectionTitle>
              <CollapseInfoAlertValue>
                {info.cumulativeImpactedUniques && info.impactedPercentage
                  ? `${info.cumulativeImpactedUniques} / ${info.impactedPercentage}%`
                  : 'NA'}
              </CollapseInfoAlertValue>
              <div>Total Devices: {info.cumulativeTotalUniques ?? 'NA'}</div>
            </CollapseInfoAlertDetailItem>
            <CollapseInfoAlertDetailItem>
              <InfoAlertSectionTitle>Impacted Count</InfoAlertSectionTitle>
              <CollapseInfoAlertValue>
                {info.impactedCounts && info.impactedCountPercentage
                  ? `${info.impactedCounts} / ${info.impactedCountPercentage}%`
                  : 'NA'}
              </CollapseInfoAlertValue>
              <div>Total Counts: {info.totalCounts ?? 'NA'}</div>
            </CollapseInfoAlertDetailItem>
          </>
        )}
      </CollapsedInfoLine>
      <CollapseInfoAlertSectionTitle>
        Root Cause Leads {<AICohortDrawerButton />}
      </CollapseInfoAlertSectionTitle>
      <Tooltip
        title={info.displayRootCauses.reduce((allString, item, index) => {
          return (
            allString +
            item.value +
            ` (${item.rootCause})` +
            (index !== info.displayRootCauses.length - 1 ? ' and ' : '')
          );
        }, '')}
        placement="top"
      >
        <CollapseInfoAlertCohortSectionBody>
          {info.displayRootCauses.map((item, index) => (
            <span key={index}>
              <CollapseInfoAlertRootCause>{item.value} </CollapseInfoAlertRootCause>
              <CollapseInfoAlertRootCause>
                ({item.rootCause}) {index !== info.displayRootCauses.length - 1 ? 'and ' : null}
              </CollapseInfoAlertRootCause>
            </span>
          ))}
        </CollapseInfoAlertCohortSectionBody>
      </Tooltip>
    </CollapsedAlertInfo>
  );
}
